import { sections } from '#packages/util';
import { waitForAddedCompRef } from '#packages/componentsAddUtils';
import { getPageSections } from '../../api';
import { fedopsLogger } from '#packages/util';

import type { Scope } from '#packages/sections';
import type { CompRef } from 'types/documentServices';

const wrapPageCompsInSection = async (
  scope: Scope,
  pageRef: CompRef,
  fedopsOrigin?: string,
) => {
  const { components, editorAPI } = scope;
  const pageSections = getPageSections(scope, pageRef);

  if (pageSections.length) return;

  if (fedopsOrigin) {
    // gathering data for https://jira.wixpress.com/browse/WEED-31930
    // can be removed once ticket is resolved
    fedopsLogger.interactionStarted('legacy_sections_enforcement', {
      customParams: { origin: fedopsOrigin },
    });
  }

  const { height: pageHeight } = editorAPI.components.layout.get_size(pageRef);

  const pageComponents = components
    .getChildren(pageRef)
    .filter((compRef) => !components.layout.isPinned(compRef));

  const { title } = editorAPI.pages.data.get(pageRef.id);

  const pageHeightByComponents = Math.max(
    ...pageComponents
      .map(components.layout.get)
      .map((layout) => layout.y + layout.height),
  );

  const blankSectionStructure = sections.getBlankSectionStructure({
    y: 0,
    height: Math.max(
      pageHeight,
      pageHeightByComponents,
      sections.constants.SECTION_MIN_HEIGHT,
    ),
    name: title,
  });

  const sectionRef = await waitForAddedCompRef(
    components.add(pageRef, blankSectionStructure),
  );

  const ignorePostUpdateSelection = true;

  pageComponents.forEach((compRef) =>
    components.setContainer([compRef], sectionRef, ignorePostUpdateSelection),
  );

  await editorAPI.waitForChangesAppliedAsync();

  editorAPI.components.hooks.componentAddedToStage.fire({
    type: 'enforceSection',
    compRef: sectionRef,
    origin: 'enforceSection',
  });

  if (fedopsOrigin) {
    fedopsLogger.interactionEnded('legacy_sections_enforcement');
  }
};

export const enforceSectionContainer = async (
  scope: Scope,
  pageRef: CompRef,
  fedopsOrigin?: string,
) => {
  const { editorAPI, history } = scope;
  await wrapPageCompsInSection(
    scope,
    { ...pageRef, type: 'DESKTOP' },
    fedopsOrigin,
  );

  if (pageRef.type === 'MOBILE') {
    if (getPageSections(scope, pageRef).length === 0) {
      editorAPI.mobileConversion.runMobileMergeOnPageById(pageRef.id);
    }
  }

  await editorAPI.waitForChangesAppliedAsync();
  history.amend();
};

export const isAllowedToRunEnforcement = (scope: Scope, pageRef: CompRef) => {
  if (scope.sectionsMigrationApi.isPerPageMigrationEnabled()) return false;
  if (!pageRef?.id) return false;

  const pageData = scope.editorAPI.pages.data.get(pageRef.id);

  if (!pageData) {
    return false;
  }
  const { isPopup, managingAppDefId, tpaPageId } = pageData;

  if (isPopup) return false;
  if (!managingAppDefId && !tpaPageId) return false;

  return true;
};
